// Descriptive evidence coverage, never a probability or a validation gate.
export const VALIDATION_THRESHOLD = 70; // Compatibility only; human review validates a problem.
const TYPE_WEIGHT = { interview: 10, usability: 10, ab_test: 14, analytics: 10, cohort: 8, survey: 6, support_ticket: 4, tech_spike: 6, feasibility_doc: 6, architecture: 4, pricing: 8, finance: 6, repo_link: 2, heatmap: 4, link: 2 };
const clamp = (n, low, high) => Math.max(low, Math.min(high, n));
export const evidenceRelationship = evidence => ['supports', 'contradicts', 'neutral'].includes(evidence?.relationship) ? evidence.relationship : 'unreviewed';
const sourceKey = (e, index) => {
  const identity = String(e.source_identity || e.customer_id || e.account_id || '').trim().toLowerCase();
  if (identity) return 'identity:' + identity;
  const url = String(e.source_url || e.url || '').trim();
  if (url) { try { const parsed = new URL(url); parsed.hash = ''; return 'url:' + parsed.href; } catch { return 'url:' + url; } }
  return 'record:' + (e.id || index);
};
export function scoreEvidenceForProblem(allEvidence = [], problemId) {
  const items = allEvidence.filter(e => String(e.problem_card_id ?? e.problem_id ?? e.problemId ?? e.problem) === String(problemId));
  const groups = { supports: new Map(), contradicts: new Map(), neutral: new Map(), unreviewed: new Map() };
  let lastEvidenceDays = null;
  let unidentifiedCount = 0;
  const sources = new Set();
  items.forEach((e, index) => {
    const date = e.observed_at || e.dateISO || e.created_at;
    const elapsed = date ? (Date.now() - new Date(date).getTime()) / 86400000 : NaN;
    const days = Number.isFinite(elapsed) && elapsed >= 0 ? Math.floor(elapsed) : null;
    if (days !== null) lastEvidenceDays = lastEvidenceDays === null ? days : Math.min(lastEvidenceDays, days);
    const recency = days === null ? 1 : days <= 14 ? 1.2 : days <= 30 ? 1.1 : days >= 180 ? 0.85 : 1;
    const strength = Number.isFinite(Number(e.strength)) ? Number(e.strength) : 3;
    const weight = (TYPE_WEIGHT[e.type] || 2) * recency * (0.8 + clamp(strength, 1, 5) * 0.06);
    const key = sourceKey(e, index);
    const group = groups[evidenceRelationship(e)];
    group.set(key, Math.max(weight, group.get(key) || 0));
    sources.add(key);
    if (!e.source_identity && !e.customer_id && !e.account_id) unidentifiedCount++;
  });
  const normalize = values => clamp(Math.round([...values].reduce((a, b) => a + b, 0) / 120 * 100), 0, 100);
  const coverage = new Map();
  Object.values(groups).forEach(group => group.forEach((weight, key) => coverage.set(key, Math.max(weight, coverage.get(key) || 0))));
  const supportingCount = groups.supports.size;
  const contradictingCount = groups.contradicts.size;
  const unreviewedCount = groups.unreviewed.size;
  const suggestions = [];
  if (unreviewedCount) suggestions.push('Assess unreviewed evidence before drawing a conclusion');
  if (contradictingCount) suggestions.push('Review contradictory findings and explain the trade-off');
  if (!supportingCount) suggestions.push('Identify evidence that supports the problem');
  if (unidentifiedCount) suggestions.push('Identify sources to distinguish repeated observations');
  if (lastEvidenceDays > 60) suggestions.push('Check whether older findings still apply');
  return { score: normalize(groups.supports.values()), coverageScore: normalize(coverage.values()),
    supportingCount, contradictingCount, neutralCount: groups.neutral.size, unreviewedCount,
    sourceCount: sources.size, recordCount: items.length, unidentifiedCount, lastEvidenceDays, suggestions };
}
